import React, { useCallback, useEffect } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import { Autocomplete, Button, Paper, Skeleton, Stack, TextField } from "@mui/material";
import { useActionHook, useModals } from "hooks";
import { useReportsActions, useReportsQueriesSelector } from "store";

export const SetPicker: React.FC = () => {
  const {
    getCashflowSetsAction,
    deleteCashflowSetAction,
    createCashflowSetAction,
    updateCashflowAumAction,
    setQueriesAction
  } = useReportsActions();
  const { createModal, deleteModal } = useModals();

  const { cashflowSetId, currency, filters, dateRange, exchangeMethod } = useReportsQueriesSelector();

  const {
    data = [],
    loading,
    setData,
    action: getSets
  } = useActionHook(getCashflowSetsAction, {
    onSuccess: (data) => {
      // if selected existing set leave it as is
      if (cashflowSetId && data.some((set) => set.id === cashflowSetId)) return;
      // if no set selected, select the first one
      setQueriesAction({ cashflowSetId: data[0].id });
    }
  });

  const handleCreateSet = useCallback(async () => {
    createModal({
      title: "Create New Set",
      action: async (name: string) => {
        const params = JSON.stringify({ currency, filters, dateRange, exchangeDate: exchangeMethod });
        const newSet = await createCashflowSetAction(name, params);
        // Create aum for the new set - SHOULD BE DONE ON THE BACKEND
        await updateCashflowAumAction({ set_id: newSet.id, total_aum: 0, aum_enabled: false });
        setData((prevState) => [...(prevState || []), newSet]);
        setQueriesAction({ cashflowSetId: newSet.id });
      }
    });
  }, [createModal, setData, cashflowSetId, currency, filters, dateRange, exchangeMethod, createCashflowSetAction]);

  const handleDeleteSet = useCallback(
    async (id: number) => {
      const setToDelete = data?.find((set) => set.id === id);
      if (!setToDelete) return;
      deleteModal({
        message: `Are you sure you want to delete this set "${setToDelete.name}"?`,
        action: async () => {
          await deleteCashflowSetAction(id);
          setData((prevState) => {
            if (!prevState?.length) return [];
            const updatedState = prevState.filter((set) => set.id !== id);

            if (cashflowSetId === id) {
              setQueriesAction({ cashflowSetId: updatedState[0]?.id || null });
            }

            return updatedState;
          });
        }
      });
    },
    [deleteCashflowSetAction, cashflowSetId, setData, deleteModal]
  );

  useEffect(() => {
    getSets();
  }, []);

  // Server guarantees that there is at least one set (default one)
  if (loading || !data.length || !cashflowSetId) {
    return <Skeleton variant="rectangular" sx={{ borderRadius: "8px", height: 32, width: 200 }} />;
  }

  const currentSet = data.find((set) => set.id === cashflowSetId);

  return (
    <Autocomplete
      disablePortal
      options={data}
      value={currentSet}
      disableClearable
      disabled={loading}
      onChange={(_, newValue) => {
        setQueriesAction({
          cashflowSetId: newValue?.id || null,
          filters: newValue?.params ? JSON.parse(newValue.params).filters : {},
          dateRange: newValue?.params ? JSON.parse(newValue.params).dateRange : dateRange,
          exchangeMethod: newValue?.params ? JSON.parse(newValue.params).exchangeMethod : "toDate",
          currency: newValue?.params ? JSON.parse(newValue.params).currency : currency
        });
      }}
      sx={{
        width: 200,
        "& .MuiInputBase-root": {
          height: 32
        }
      }}
      renderOption={(props, option) => (
        <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {option.user_id && <PersonIcon sx={{ fontSize: 20, color: "#92A7BC" }} />}
            <span>{option.name}</span>
          </Stack>
          {option.user_id && (
            <DeleteIcon
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSet(option.id);
              }}
              sx={{
                fontSize: 20,
                color: "#92A7BC",
                "&:hover": { color: "#071F35" }
              }}
            />
          )}
        </li>
      )}
      getOptionLabel={(option) => option.name}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{
            "& .MuiInputBase-root": {
              fontSize: "14px"
            }
          }}
        />
      )}
      slots={{
        paper: ({ children }) => (
          <Paper
            sx={{
              position: "fixed",
              width: "305px",
              backgroundColor: "white",
              zIndex: 1300,
              borderRadius: "10px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
              "& .MuiAutocomplete-listbox": {
                "& .MuiAutocomplete-option[aria-disabled='true']": {
                  opacity: 1,
                  "&.Mui-focused": {
                    opacity: 1
                  }
                },
                "& .MuiAutocomplete-option[aria-selected='true']": {
                  bgcolor: "#edf1f9",
                  "&.Mui-focused": {
                    bgcolor: "#edf1f9"
                  }
                }
              },
              "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                bgcolor: "#edf1f9"
              }
            }}
          >
            {children}
            <Button
              onMouseDown={handleCreateSet}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                height: "40px",
                backgroundColor: "#fff",
                boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                borderRadius: "0 0 10px 10px",
                "&:hover": {
                  backgroundColor: "#edf1f9"
                }
              }}
              fullWidth
            >
              Add Set
            </Button>
          </Paper>
        )
      }}
    />
  );
};
